import { z } from 'zod'

/**
 * Zod schema for aircraft type form validation
 */
export const aircraftTypeFormSchema = z.object({
  // Required identification fields
  type_code: z
    .string()
    .min(1, 'Type code is required')
    .max(50, 'Type code must be 50 characters or less')
    .regex(/^[a-z0-9-]+$/i, 'Only letters, numbers, and hyphens allowed'),
  manufacturer: z
    .string()
    .min(1, 'Manufacturer is required')
    .max(100, 'Manufacturer must be 100 characters or less'),
  model: z
    .string()
    .min(1, 'Model is required')
    .max(100, 'Model must be 100 characters or less'),
  variant: z.string().max(50).optional().or(z.literal('')),

  // Category
  category: z
    .enum([
      'single_engine_piston',
      'multi_engine_piston',
      'turboprop',
      'very_light_jet',
      'light_jet',
      'midsize_jet',
      'super_midsize_jet',
      'large_jet',
      'ultra_long_range',
      'helicopter',
    ])
    .optional()
    .nullable(),

  // Required dimensions (feet)
  length: z.coerce.number().positive('Length must be positive'),
  wingspan: z.coerce.number().positive('Wingspan must be positive'),
  height: z.coerce.number().positive('Height must be positive'),
  tail_height: z
    .union([
      z.coerce.number().positive('Tail height must be positive'),
      z.literal('').transform(() => undefined),
    ])
    .optional(),

  // Weight (pounds)
  empty_weight: z
    .union([
      z.coerce.number().positive('Weight must be positive'),
      z.literal('').transform(() => undefined),
    ])
    .optional(),
  max_takeoff_weight: z
    .union([
      z.coerce.number().positive('Weight must be positive'),
      z.literal('').transform(() => undefined),
    ])
    .optional(),
  max_ramp_weight: z
    .union([
      z.coerce.number().positive('Weight must be positive'),
      z.literal('').transform(() => undefined),
    ])
    .optional(),

  // 3D Model settings
  model_file_path: z.string().max(255).optional().or(z.literal('')),
  model_scale: z
    .union([
      z.coerce.number().positive('Scale must be positive'),
      z.literal('').transform(() => 1.0),
    ])
    .optional()
    .default(1.0),
  heading_axis: z.enum(['x', 'y', 'z', '-x', '-y', '-z']).optional().default('y'),
  vertical_axis: z.enum(['x', 'y', 'z', '-x', '-y', '-z']).optional().default('z'),

  // Pricing
  typical_value_new: z
    .union([
      z.coerce.number().positive(),
      z.literal('').transform(() => undefined),
    ])
    .optional(),
  typical_value_used: z
    .union([
      z.coerce.number().positive(),
      z.literal('').transform(() => undefined),
    ])
    .optional(),
  hangar_rate_typical: z
    .union([
      z.coerce.number().positive(),
      z.literal('').transform(() => undefined),
    ])
    .optional(),

  // Metadata
  notes: z.string().max(1000).optional().or(z.literal('')),
  is_active: z.boolean().optional().default(true),

  // Landing Gear / Towing fields
  gear_type: z.enum(['tricycle', 'conventional', 'tandem']).optional().nullable(),
  main_gear_wheels: z
    .union([
      z.coerce.number().int().positive(),
      z.literal('').transform(() => undefined),
    ])
    .optional(),
  nose_gear_steering_angle: z
    .union([
      z.coerce.number().int().min(0).max(90),
      z.literal('').transform(() => undefined),
    ])
    .optional(),
  nose_gear_from_nose: z
    .union([
      z.coerce.number().positive(),
      z.literal('').transform(() => undefined),
    ])
    .optional(),
  main_gear_from_nose: z
    .union([
      z.coerce.number().positive(),
      z.literal('').transform(() => undefined),
    ])
    .optional(),
  wheel_track: z
    .union([
      z.coerce.number().positive(),
      z.literal('').transform(() => undefined),
    ])
    .optional(),
  wheelbase: z
    .union([
      z.coerce.number().positive(),
      z.literal('').transform(() => undefined),
    ])
    .optional(),
  turning_radius: z
    .union([
      z.coerce.number().positive(),
      z.literal('').transform(() => undefined),
    ])
    .optional(),

  // External API reference
  icao_designator: z.string().max(4).optional().or(z.literal('')),
  external_api_id: z
    .union([
      z.coerce.number().int().positive(),
      z.literal('').transform(() => undefined),
    ])
    .optional(),
})

// Input type for form defaultValues
export type AircraftTypeFormInput = z.input<typeof aircraftTypeFormSchema>

// Output type after validation/parsing
export type AircraftTypeFormValues = z.output<typeof aircraftTypeFormSchema>
